import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DEFAULT_TOASTER_LIFE,
  IWizardContext,
  WizardStepComponent,
  ZendraLocalizationServices,
} from '@zendra/shared';
import type { StandDivisionAndFusionFusedStandDetailDto } from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/stand-division-and-fusion/models';
import { EMPTY_VALUE } from '../stand-division-and-fusion.config';
import { DivisionFusionStand } from '../stand-division-and-fusion.data';
import type { ISelectStandsToDivideStepData } from '../interfaces/ISelectStandsToDivideStepData';
import type { IDividedStandRow } from '../interfaces/IDividedStandRow';
import type { StepSelectFusedStandData } from '../step-select-fused-stand/step-select-fused-stand';

interface DescriptionRow {
  id: number;
  stand: string;
  cycle: string;
  isLastCycle: boolean;
  rotation: string;
  isLastRotation: boolean;
  area: number;
}

export interface StepDescriptionAndCompletionData {
  sourceStep4Snapshot: string;
  transformationReason: string;
  initialRows: DescriptionRow[];
  finalRows: DescriptionRow[];
  initialTotalArea: number;
  finalTotalArea: number;
}

@Component({
  selector: 'lib-step-description-and-completion-stand-division-and-fusion',
  standalone: true,
  imports: [LocalizationPipe, ReactiveFormsModule],
  templateUrl: './step-description-and-completion.html',
})
export class StepDescriptionAndCompletion
  implements WizardStepComponent<StepDescriptionAndCompletionData>
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly toaster = inject(ToasterService);
  private readonly localizationService = inject(LocalizationService);

  readonly localizationKeyPrefix = 'Geographic::StandDivisionAndFusion.StepDescriptionAndCompletion';
  readonly previousStepId = 4;

  readonly selectedStands = signal<IDividedStandRow[]>([]);
  readonly fusedStand = signal<DivisionFusionStand | null>(null);
  readonly fusedStandDetail = signal<StandDivisionAndFusionFusedStandDetailDto | null>(null);
  readonly form = this.formBuilder.group({
    transformationReason: ['', [Validators.required]],
  });
  readonly transformationReason = this.form.controls.transformationReason;

  readonly initialRows = computed<DescriptionRow[]>(() =>
    this.selectedStands().map(row =>
      this.createDescriptionRow(
        Number(row.id),
        row.name,
        row.cycle,
        row.isLastCycle,
        row.rotation,
        row.isLastRotation,
        row.currentArea,
      ),
    ),
  );

  readonly finalRows = computed<DescriptionRow[]>(() => {
    const updatedRows = this.selectedStands().map(row =>
      this.createDescriptionRow(
        Number(row.id),
        row.name,
        row.cycle,
        row.isLastCycle,
        row.rotation,
        row.isLastRotation,
        row.newArea,
      ),
    );
    const fusedStandRow = this.createFusedStandRow(this.fusedStandFinalArea());

    return fusedStandRow ? [...updatedRows, fusedStandRow] : updatedRows;
  });

  readonly initialTotalArea = computed(() => this.sumRowsArea(this.initialRows()));
  readonly finalTotalArea = computed(() => this.sumRowsArea(this.finalRows()));

  onActivate(context: IWizardContext): void {
    const activeStepId = context.activeStepId();
    const selectedStandsData = context.getStepState<ISelectStandsToDivideStepData>(2)?.data;
    const fusedStandData = context.getStepState<StepSelectFusedStandData>(this.previousStepId)?.data;
    const currentState = context.getStepState<StepDescriptionAndCompletionData>(activeStepId)?.data;
    const sourceStep4Snapshot = this.buildStep4Snapshot(fusedStandData);
    const shouldRestoreCurrentData =
      !!currentState && currentState.sourceStep4Snapshot === sourceStep4Snapshot;

    context.patchStepState(activeStepId, {
      visited: true,
    });

    if (!shouldRestoreCurrentData && currentState) {
      context.patchStepState(activeStepId, {
        valid: false,
        completed: false,
        data: undefined,
        summary: [],
      });
    }

    this.selectedStands.set(selectedStandsData?.selectedStands ?? []);
    this.fusedStand.set(fusedStandData?.fusedStand ?? null);
    this.fusedStandDetail.set(fusedStandData?.fusedStandDetail ?? null);
    this.transformationReason.reset(
      shouldRestoreCurrentData ? currentState?.transformationReason ?? '' : '',
      {
        emitEvent: false,
      },
    );
  }

  canLeave(): boolean {
    this.transformationReason.markAsTouched();
    if (!this.transformationReason.valid) {
      this.notifyRequiredFields();
      return false;
    }

    return true;
  }

  canLeaveSync(): boolean {
    return true;
  }

  collect(context: IWizardContext): StepDescriptionAndCompletionData {
    const activeStepId = context.activeStepId();
    const fusedStandData = context.getStepState<StepSelectFusedStandData>(this.previousStepId)?.data;
    const data: StepDescriptionAndCompletionData = {
      sourceStep4Snapshot: this.buildStep4Snapshot(fusedStandData),
      transformationReason: this.transformationReason.value?.trim() ?? '',
      initialRows: this.initialRows(),
      finalRows: this.finalRows(),
      initialTotalArea: this.initialTotalArea(),
      finalTotalArea: this.finalTotalArea(),
    };

    context.patchStepState(activeStepId, {
      visited: true,
      valid: this.transformationReason.valid,
      completed: true,
      data,
    });

    return data;
  }

  fieldInvalid(field: 'transformationReason'): boolean {
    const control = this.form.get(field);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  formatArea(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return EMPTY_VALUE;
    }

    return value.toFixed(2).replace('.', ',');
  }

  formatCycleLabel(value: string | null | undefined, isLastCycle: boolean): string {
    return this.formatCycleOrRotationLabel(
      value,
      isLastCycle,
      'Geographic::Entity.Cycle.IsLastCycle',
    );
  }

  formatRotationLabel(value: string | null | undefined, isLastRotation: boolean): string {
    return this.formatCycleOrRotationLabel(
      value,
      isLastRotation,
      'Geographic::Entity.Rotation.IsLastRotation',
    );
  }

  private createDescriptionRow(
    id: number,
    stand: string,
    cycle: string | null,
    isLastCycle: boolean | null | undefined,
    rotation: string | null,
    isLastRotation: boolean | null | undefined,
    area: number,
  ): DescriptionRow {
    return {
      id,
      stand,
      cycle: cycle ?? EMPTY_VALUE,
      isLastCycle: isLastCycle === true,
      rotation: rotation ?? EMPTY_VALUE,
      isLastRotation: isLastRotation === true,
      area,
    };
  }

  private createFusedStandRow(area: number | null): DescriptionRow | null {
    const detail = this.fusedStandDetail();
    const fusedStand = this.fusedStand();
    const standId = Number(detail?.id ?? fusedStand?.id);

    if (!fusedStand || Number.isNaN(standId) || area === null) {
      return null;
    }

    return this.createDescriptionRow(
      standId,
      detail?.name ?? fusedStand.name,
      this.formatNumericValue(detail?.cycleValue) ?? fusedStand.cycle,
      detail?.isLastCycle,
      this.formatNumericValue(detail?.rotationValue) ?? fusedStand.rotation,
      detail?.isLastRotation,
      area,
    );
  }

  private fusedStandInitialArea(): number | null {
    const detailArea = this.fusedStandDetail()?.standArea;
    if (detailArea !== null && detailArea !== undefined) {
      return detailArea;
    }

    return this.fusedStand()?.currentArea ?? null;
  }

  private fusedStandFinalArea(): number | null {
    const initialArea = this.fusedStandInitialArea();
    if (initialArea === null) {
      return null;
    }

    const transferredArea = this.selectedStands().reduce(
      (sum, row) => sum + this.roundArea(row.sectionedArea),
      0,
    );

    return this.roundArea(initialArea + transferredArea);
  }

  private sumRowsArea(rows: DescriptionRow[]): number {
    return this.roundArea(rows.reduce((sum, row) => sum + row.area, 0));
  }

  private buildStep4Snapshot(value?: StepSelectFusedStandData): string {
    return JSON.stringify({
      sourceStep2Snapshot: value?.sourceStep2Snapshot ?? null,
      fusedStandId: value?.fusedStandDetail?.id ?? value?.fusedStand?.id ?? null,
      fusedStandArea: this.roundArea(
        value?.fusedStandDetail?.standArea ?? value?.fusedStand?.currentArea ?? 0,
      ),
      cycleValue: value?.fusedStandDetail?.cycleValue ?? value?.fusedStand?.cycle ?? null,
      isLastCycle: value?.fusedStandDetail?.isLastCycle ?? null,
      rotationValue:
        value?.fusedStandDetail?.rotationValue ?? value?.fusedStand?.rotation ?? null,
      isLastRotation: value?.fusedStandDetail?.isLastRotation ?? null,
      landContractId: value?.fusedStandDetail?.cycleRotation?.landContractId ?? null,
      fomentContractId: value?.fusedStandDetail?.cycleRotation?.fomentContractId ?? null,
      propertyTypeId: value?.fusedStandDetail?.cycleRotation?.propertyTypeId ?? null,
    });
  }

  private notifyRequiredFields(): void {
    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::StandDivisionAndFusion.StepDescriptionAndCompletion.RequiredFields`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
  }

  private roundArea(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private formatNumericValue(value: number | null | undefined): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    return String(value);
  }

  private formatCycleOrRotationLabel(
    value: string | null | undefined,
    isLastValue: boolean,
    suffixKey: string,
  ): string {
    const formattedValue = value?.trim() ? value : EMPTY_VALUE;
    if (!isLastValue) {
      return formattedValue;
    }

    return `${formattedValue} - ${this.localizationService.instant(suffixKey)}`;
  }
}
